/**
 * useContractorInterest — thin hook wrapping the interest engine.
 *
 * Pure delegation to contractorInterest.ts. Caller emits no offer or
 * assignment side-effects; interest is read-only signal to the dispatcher.
 */

import { useCallback } from "react";
import { toast } from "sonner";
import { useAppData } from "@/data/AppDataContext";
import { MarketplaceService } from "@/services/marketplace.service";
import {
  submitInterest,
  dismissInterest,
  type InterestSubmissionResult,
} from "@/data/contractor/contractorInterest";
import { INTEREST_TOAST } from "@/data/contractor/contractorMessaging";

export function useContractorInterest() {
  const { contractorInterests, setContractorInterests, appendSystemEvents } = useAppData();

  const submit = useCallback(
    (jobId: string, contractorId: string, actor?: string): InterestSubmissionResult => {
      let result: InterestSubmissionResult = {
        interests: contractorInterests,
        events: [],
        interest: {
          id: "",
          jobId,
          contractorId,
          submittedAt: "",
          status: "open",
        },
        created: false,
      };
      setContractorInterests((prev) => {
        result = submitInterest(prev, {
          jobId,
          contractorId,
          actor: actor ?? contractorId,
        });
        return result.created ? result.interests : prev;
      });
      if (result.events.length) appendSystemEvents(result.events);
      if (result.created) {
        void MarketplaceService.insertInterest(result.interest).catch((error) => {
          console.warn("[useContractorInterest] Failed to persist submitted interest:", error);
          toast.error("Interest persistence failed", {
            description: "Request was created locally but failed to save to database.",
          });
        });
      }
      // P4: action toast fires from the hook (single canonical location).
      if (result.created) {
        toast.success(INTEREST_TOAST.submitted.title, {
          description: INTEREST_TOAST.submitted.description,
        });
      } else {
        toast(INTEREST_TOAST.duplicate.title, {
          description: INTEREST_TOAST.duplicate.description,
        });
      }
      return result;
    },
    [setContractorInterests, appendSystemEvents, contractorInterests],
  );

  const dismiss = useCallback(
    (interestId: string, actor: string) => {
      setContractorInterests((prev) => {
        const r = dismissInterest(prev, interestId, actor);
        if (r.closedCount === 0) return prev;
        void MarketplaceService.dismissInterest(interestId).catch((error) => {
          console.warn("[useContractorInterest] Failed to persist dismissed interest:", error);
          toast.error("Interest update failed", {
            description: "Dismissal was applied locally but failed to save to database.",
          });
        });
        appendSystemEvents(r.events);
        return r.interests;
      });
    },
    [setContractorInterests, appendSystemEvents],
  );

  return { interests: contractorInterests, submit, dismiss };
}
